import { Link, CircularProgress, createTheme, ThemeProvider } from "@mui/material";
import { Alert } from "@mui/material";
import { Units, Temperatureunits, User, Userupdate } from "@inductosense/typescript-fetch";
import RDCTestPanel from "RDC/RDCTestPanel";
import * as React from "react";
import { Component } from "react";
import RRCPanel from "RRC/RRCPanel";
import * as MainCGI from "Services/Electron/MainCGI";
import Services from "Services/Platform/Services";
import UiBlockingState from "Types/UiBlockingState";
import View from "Types/View";
import UiBlockingOverlay from "Views/AsyncOverlay/UiBlockingOverlay";
import GatewaysView from "Views/Gateways/GatewaysView";
import MainDashboard from "Views/MainDashboard";
import MaterialsView from "Views/Materials/MaterialsView";
import RecalculationDashboard from "Views/Recalculation";
import DcScanningOverviewForm from "../DC/DcScanningOverviewForm";
import UiSettings from "../Model/UiSettings";
import LoginFormModalPanel from "../Panels/Composite/Modals/LoginFormModalPanel";
import FlexibleAndFixedPanel from "../Panels/FlexibleAndFixedPanel";
import ModalPanel from "../Panels/ModalPanel";
import RDCScanningOverviewForm from "../RDC/RDCScanningOverviewForm";
import SpiderPanel from "../Spider/SpiderPanel";
import DeploymentsDashboard from "./DeploymentsDashboard";
import ExportDashboard from "./Export";
import ImportDashboard from "./Import";
import InstallConfigScreen from "./InstallConfigScreen";
import UserListView from "./UserListPanel";
import { manifest, ManifestType } from "./manifest";
import ServiceInstallNotification, { ServiceInstallResult } from "../Components/Notifications/ServiceInstallNotification";
import V3ReleaseNotification from "../Components/Notifications/V3ReleaseNotification";
import Idart2ServiceWarningNotification from "../Components/Notifications/Idart2ServiceWarningNotification";
import UnapprovedHardwareNotification from "../Components/Notifications/UnapprovedHardwareNotification";
import UnsyncedDataNotification from "../Components/Notifications/UnsyncedDataNotification";

interface WindowsServiceRequiredResult {
    serviceRequired: boolean;
    hasV3Devices: boolean;
    isApproved: boolean;
    isInstalled: boolean;
    isIdart2ServiceInstalled: boolean;
    idart2ServiceTenantName?: string;
    deviceSerialNumber?: string;
    error?: string;
}
import FirstLoginModal, { LINK_MICROSOFT_KEY, LinkMicrosoftState } from "../Panels/Composite/Modals/FirstLoginModal";
import Button from "Components/Buttons/Button";

interface UnsyncedDataCheckResult {
    hasUnsyncedData: boolean;
    unsyncedReadings: number;
    unsyncedPayloads: number;
    needsLegacyCleanup: boolean;
    error?: string;
}

interface InstalledServiceTenantStatusResult {
    isInstalled: boolean;
    cleanupPending: boolean;
    installedTenantName: string | null;
    source: string;
    error?: string;
}

interface Idart2ServiceModeResult {
    isIdart2ServiceInstalled: boolean;
    idart2ServiceTenantName?: string;
    error?: string;
}

interface LegacyCleanupResult {
    success?: boolean;
    skipped?: boolean;
    reason?: string;
    error?: string;
}

interface TopLevelViewProps {
    onReadyToRender(): void;
    onFailedToAuthenticate(response: Response): void;
}

interface TopLevelViewState {
    isAuthenticated: boolean;
    isSyncingData: boolean;
    isShowingDbUpdateModal: boolean;
    isInitialLoad: boolean;
    isAuthenticating: boolean;
    uiBlockingState: UiBlockingState;
    activeView: View;
    loginErrorMessage?: string;
    progressTextLines: string[];
    uiSettings?: UiSettings;
    serverConnectionDetected: boolean;
    tabletMode: boolean;
    syncErrors?: object[];
    showingErrors: boolean;
    availableTenants?: string[];
    tenantName?: string;
    manifest: ManifestType;
    areGatewaysEnabled: boolean;
    // Service installation state
    serviceInstallState: "checking" | "installing" | "success" | "deferred" | "error" | "idle";
    serviceInstallError?: string;
    serviceInstalledVersion?: string;
    showV3ReleaseNotification: boolean;
    showIdart2ServiceWarning: boolean;
    isIdart2ServiceInstalled: boolean;
    idart2ServiceTenantName?: string;
    showIdart2DeviceSyncModal: boolean;
    deviceSyncCompatibilityStatus: "checking" | "allowed" | "idart2" | "unavailable";
    showOnlineDeviceSyncCompatibilityModal: boolean;
    pendingOnlineDeviceView?: "rdcScanning" | "wandScanning";
    showOfflineDeviceSyncCheckError: boolean;
    pendingOfflineDeviceView?: "rdcScanning" | "wandScanningOffline";
    pendingOfflineDeviceLocalDbName?: string;
    showUnapprovedHardwareNotification: boolean;
    unapprovedHardwareDeviceSerialNumber?: string;
    unapprovedHardwareAuthToken?: string;
    unapprovedHardwareApiUrl?: string;
    // Unsynced data notification state
    showUnsyncedDataNotification: boolean;
    unsyncedReadings: number;
    unsyncedPayloads: number;
    showFirstLoginModal: boolean;
    currentUsername?: string;
    currentEmail?: string;
    pendingMicrosoftLinking: boolean;
    microsoftLinkingFailed: boolean;
    microsoftLinkingErrorDetail?: string;
    pendingLinkState?: LinkMicrosoftState;
    // Pre-login legacy cleanup notice state
    showLegacyCleanupNotice: boolean;
    isLegacyCleanupRunning: boolean;
    legacyCleanupError?: string;
    isTenantCompatibleForSignIn: boolean;
    tenantCompatibilityMessage?: string;
    isTenantCompatibilityChecking: boolean;
    isLoginInProgress: boolean;
    loginProgressMessage?: string;
}

const prefixCls = "toplevel"

class TopLevelView extends Component<TopLevelViewProps, TopLevelViewState> {
    private readonly SERVICE_CHECKING_SHOW_DELAY_MS = 400;
    private tenantCompatibilityCheckToken = 0;
    private loginInProgressLock = false;

    constructor(props: TopLevelViewProps) {
        super(props);

        this.state = {
            isAuthenticated: false,
            isAuthenticating: false,
            isInitialLoad: true,
            isSyncingData: false,
            isShowingDbUpdateModal: false,
            uiBlockingState: false,
            activeView: "dashboard",
            serverConnectionDetected: false,
            progressTextLines: [],
            tabletMode: false,
            showingErrors: false,
            tenantName: undefined,
            manifest: {
                loginScreenMessage: null,
                showPortalLink: false,
                disableUsersScreen: false,
                showMicrosoftLogin: false,
                slides: [],
                deviceHomeImgs: [],
                serverOptions: []
            },
            areGatewaysEnabled: false,
            serviceInstallState: "idle",
            serviceInstallError: undefined,
            serviceInstalledVersion: undefined,
            showV3ReleaseNotification: false,
            showIdart2ServiceWarning: false,
            isIdart2ServiceInstalled: false,
            idart2ServiceTenantName: undefined,
            showIdart2DeviceSyncModal: false,
            deviceSyncCompatibilityStatus: "checking",
            showOnlineDeviceSyncCompatibilityModal: false,
            pendingOnlineDeviceView: undefined,
            showOfflineDeviceSyncCheckError: false,
            pendingOfflineDeviceView: undefined,
            pendingOfflineDeviceLocalDbName: undefined,
            showUnapprovedHardwareNotification: false,
            unapprovedHardwareDeviceSerialNumber: undefined,
            unapprovedHardwareAuthToken: undefined,
            unapprovedHardwareApiUrl: undefined,
            showUnsyncedDataNotification: false,
            unsyncedReadings: 0,
            unsyncedPayloads: 0,
            showFirstLoginModal: false,
            currentUsername: undefined,
            pendingMicrosoftLinking: false,
            microsoftLinkingFailed: false,
            microsoftLinkingErrorDetail: undefined,
            pendingLinkState: undefined,
            showLegacyCleanupNotice: false,
            isLegacyCleanupRunning: false,
            legacyCleanupError: undefined,
            isTenantCompatibleForSignIn: true,
            tenantCompatibilityMessage: undefined,
            isTenantCompatibilityChecking: false,
            isLoginInProgress: false,
            loginProgressMessage: undefined
        };
    }

    async componentDidMount() {
        this.setState({
            isAuthenticating: false,
            isAuthenticated: false,
            isInitialLoad: false,
            tabletMode: localStorage.getItem("tabletMode") === "true"
        });

        this.setState({ manifest });

        // Check for unsynced data that blocks cleanup (runs on every startup)
        this.checkUnsyncedDataAsync();

        setInterval(async () => {
            if (Services.isSessionActive()) {
                await Services.sendAliveMessage();
            }
        }, 3000);

        // Check if we're returning from Microsoft OAuth for account linking
        await this.checkMicrosoftLinkingReturn();

        try {
            const isElectron = location.origin === "file://";
            const customServerUrl = localStorage.getItem("customServerUrl");

            let manifestUrl: string | null = null;

            if (isElectron) {
                // Desktop app: derive manifest URL from the configured server base URL.
                // Never reach out to the cloud CDN from the desktop app.
                if (customServerUrl) {
                    const serverBase = customServerUrl.replace(/\/v1\/?$/, "");
                    manifestUrl = `${serverBase}/manifest.json`;
                }
                // If no custom URL configured yet, skip fetch and keep local defaults.
            } else {
                // Web app: always try the origin first (works for both on-prem and cloud
                // web deployments since manifest.json is now bundled into the build output).
                // Fall back to the cloud CDN for deployments that predate this change.
                manifestUrl = `${location.origin}/manifest.json`;
            }

            if (manifestUrl) {
                let response = await fetch(manifestUrl, { cache: "no-store" });

                if (!response.ok && !isElectron) {
                    // Origin fetch failed — fall back to cloud CDN
                    console.log("Origin manifest fetch failed, falling back to cloud CDN");
                    response = await fetch("https://static.inductosense.cloud/idart/manifest.json", { cache: "no-store" });
                }

                if (response.ok) {
                    const manifestOnline: ManifestType = await response.json();
                    console.log("manifestOnline", manifestOnline);
                    this.setState({ manifest: manifestOnline });

                    // If the manifest specifies a default server and none has been
                    // configured yet, write it to localStorage so the app uses it
                    // immediately without the user needing to visit settings.
                    if (manifestOnline.defaultServerUrl && !localStorage.getItem("customServerUrl")) {
                        localStorage.setItem("customServerUrl", manifestOnline.defaultServerUrl);
                        console.log("Default server URL applied from manifest:", manifestOnline.defaultServerUrl);
                    }
                }
            }
        }
        catch {
            console.log("Manifest fetch failed, using default");
        }
    }

    async componentDidUpdate(_prevProps: TopLevelViewProps, prevState: TopLevelViewState) {
        if (this.state.activeView === "dashboard" && prevState.activeView !== "dashboard") {
            await this.checkServerAccessible();
        }

        if (this.state.isAuthenticating !== prevState.isAuthenticating) {
            await this.checkServerAccessible();
        }
    }

    async checkServerAccessible() {
        try {
            this.setState({ serverConnectionDetected: false });

            const urlPrefix = await Services.getUrlPrefix();
            const response = await fetch(urlPrefix + "/sessions/active");

            if (response.ok) {  // Check if HTTP status is 2xx
                this.setState({ serverConnectionDetected: true });
            } else {
                console.error("Server responded with status: ", response.status);
            }
        } catch (error) {
            console.error("Error during fetch operation: ", error);
            // Handle error, provide user feedback
        }
    }

    private normalizeTenantNameForComparison(value?: string | null): string {
        return (value ?? "")
            .trim()
            .replace(/\s+/g, "-")
            .toLowerCase();
    }

    private areTenantNamesCompatible(selectedTenant: string, installedTenant: string): boolean {
        return this.normalizeTenantNameForComparison(selectedTenant) ===
            this.normalizeTenantNameForComparison(installedTenant);
    }

    private parseInstalledServiceTenantStatus(raw: any): InstalledServiceTenantStatusResult {
        return {
            isInstalled: raw?.isInstalled ?? raw?.IsInstalled ?? false,
            cleanupPending: raw?.cleanupPending ?? raw?.CleanupPending ?? false,
            installedTenantName: raw?.installedTenantName ?? raw?.InstalledTenantName ?? null,
            source: raw?.source ?? raw?.Source ?? "none",
            error: raw?.error ?? raw?.Error
        };
    }

    private parseIdart2ServiceMode(raw: any): Idart2ServiceModeResult {
        return {
            isIdart2ServiceInstalled: raw?.isIdart2ServiceInstalled ?? raw?.IsIdart2ServiceInstalled ?? false,
            idart2ServiceTenantName: raw?.idart2ServiceTenantName ?? raw?.Idart2ServiceTenantName,
            error: raw?.error ?? raw?.Error
        };
    }

    async onRequestOfflineDeviceView(view: "rdcScanning" | "wandScanningOffline", localDbName: string) {
        if (!MainCGI.isServiceAvailable()) {
            this.openOfflineDeviceView(view, localDbName);
            return;
        }

        const serviceUpdateApi = window.serviceUpdateApi;
        if (!serviceUpdateApi || !serviceUpdateApi.checkIdart2ServiceMode) {
            this.blockOfflineDeviceScanning(view, localDbName, "The Windows Service mode could not be verified.");
            return;
        }

        try {
            const result = this.parseIdart2ServiceMode(await serviceUpdateApi.checkIdart2ServiceMode());
            if (result.error) {
                console.error("iDART2 Windows Service mode check failed:", result.error);
                this.blockOfflineDeviceScanning(view, localDbName, result.error);
                return;
            }

            if (result.isIdart2ServiceInstalled) {
                this.setState({
                    isIdart2ServiceInstalled: true,
                    idart2ServiceTenantName: result.idart2ServiceTenantName,
                    showIdart2DeviceSyncModal: true
                });
                return;
            }
        } catch (error) {
            console.error("iDART2 Windows Service mode check failed:", error);
            this.blockOfflineDeviceScanning(view, localDbName, String(error));
            return;
        }

        this.openOfflineDeviceView(view, localDbName);
    }

    private openOfflineDeviceView(view: "rdcScanning" | "wandScanningOffline", localDbName: string) {
        Services.LocalDbName = localDbName;
        this.setState({ activeView: view });
    }

    private blockOfflineDeviceScanning(view: "rdcScanning" | "wandScanningOffline", localDbName: string, error: string) {
        console.error("Offline device scanning blocked because Windows Service mode could not be verified:", error);
        this.setState({
            showOfflineDeviceSyncCheckError: true,
            pendingOfflineDeviceView: view,
            pendingOfflineDeviceLocalDbName: localDbName
        });
    }

    private async retryOfflineDeviceSyncCheck() {
        const { pendingOfflineDeviceView, pendingOfflineDeviceLocalDbName } = this.state;
        if (!pendingOfflineDeviceView || !pendingOfflineDeviceLocalDbName) return;

        this.setState({ showOfflineDeviceSyncCheckError: false });
        await this.onRequestOfflineDeviceView(pendingOfflineDeviceView, pendingOfflineDeviceLocalDbName);
    }

    private idart2DeviceSyncModal() {
        return <ModalPanel
            shouldBeShown={this.state.showIdart2DeviceSyncModal}
            title="Sync devices in iDART2"
            content={<p>HDC and RDC synchronisation are now handled by iDART2. Please open iDART2 to sync devices.</p>}
            onClose={() => this.setState({ showIdart2DeviceSyncModal: false })}
        />;
    }

    private offlineDeviceSyncCheckErrorModal() {
        return <ModalPanel
            shouldBeShown={this.state.showOfflineDeviceSyncCheckError}
            title="Unable to start device scanning"
            content={<>
                <p>Unable to verify whether iDART2 is managing connected devices. Device scanning has not started.</p>
                <Button label="Retry" onClick={() => this.retryOfflineDeviceSyncCheck()} />
            </>}
            onClose={() => this.setState({ showOfflineDeviceSyncCheckError: false })}
        />;
    }

    private onlineDeviceSyncCompatibilityModal() {
        const isChecking = this.state.deviceSyncCompatibilityStatus === "checking";
        return <ModalPanel
            shouldBeShown={this.state.showOnlineDeviceSyncCompatibilityModal}
            title={isChecking ? "Checking device compatibility" : "Unable to start device scanning"}
            content={isChecking ?
                <p>Please wait while iDART checks whether iDART2 is managing connected devices.</p> :
                <>
                    <p>Unable to verify whether iDART2 is managing connected devices. Device scanning has not started.</p>
                    <Button label="Retry" onClick={() => this.retryOnlineDeviceSyncCompatibilityCheck()} />
                </>
            }
            onClose={() => this.setState({
                showOnlineDeviceSyncCompatibilityModal: false,
                pendingOnlineDeviceView: undefined
            })}
        />;
    }

    private async retryOnlineDeviceSyncCompatibilityCheck() {
        if (!this.state.tenantName) return;

        this.setState({
            deviceSyncCompatibilityStatus: "checking",
            showOnlineDeviceSyncCompatibilityModal: true
        });
        await this.checkWindowsServiceRequiredAsync(this.state.tenantName);
    }

    private resumePendingOnlineDeviceScan() {
        const pendingOnlineDeviceView = this.state.pendingOnlineDeviceView;
        this.setState({
            showOnlineDeviceSyncCompatibilityModal: false,
            pendingOnlineDeviceView: undefined
        }, () => {
            if (pendingOnlineDeviceView) {
                this.changeActiveView(pendingOnlineDeviceView);
            }
        });
    }

    private completeTenantCompatibilityCheck(
        checkToken: number,
        nextState: Pick<TopLevelViewState, "isTenantCompatibilityChecking" | "isTenantCompatibleForSignIn" | "tenantCompatibilityMessage">
    ): boolean {
        if (checkToken !== this.tenantCompatibilityCheckToken) {
            return false;
        }

        this.setState(nextState);
        return true;
    }

    async onTenantChosen(tenant: string) {
        const checkToken = ++this.tenantCompatibilityCheckToken;
        this.setState({
            tenantName: tenant,
            isTenantCompatibilityChecking: true,
            isTenantCompatibleForSignIn: false,
            tenantCompatibilityMessage: "Checking Background Service installation..."
        });

        const serviceUpdateApi = window.serviceUpdateApi;
        if (!serviceUpdateApi || !serviceUpdateApi.getInstalledServiceTenantStatus) {
            this.completeTenantCompatibilityCheck(checkToken, {
                isTenantCompatibilityChecking: false,
                isTenantCompatibleForSignIn: true,
                tenantCompatibilityMessage: undefined
            });
            return;
        }

        try {
            const rawResult = await Promise.race([
                serviceUpdateApi.getInstalledServiceTenantStatus(),
                new Promise((_, reject) => setTimeout(() => reject(new Error("Timed out")), 10000))
            ]);
            const result = this.parseInstalledServiceTenantStatus(rawResult);
            if (result.error) {
                this.completeTenantCompatibilityCheck(checkToken, {
                    isTenantCompatibilityChecking: false,
                    isTenantCompatibleForSignIn: false,
                    tenantCompatibilityMessage: "Unable to verify installed Background Service tenant. Sign-in will re-check before continuing."
                });
                return;
            }

            if (result.cleanupPending) {
                this.completeTenantCompatibilityCheck(checkToken, {
                    isTenantCompatibilityChecking: false,
                    isTenantCompatibleForSignIn: false,
                    tenantCompatibilityMessage: "Previous Background Service cleanup did not complete. Sign-in will retry cleanup before continuing."
                });
                return;
            }

            if (!result.isInstalled) {
                this.completeTenantCompatibilityCheck(checkToken, {
                    isTenantCompatibilityChecking: false,
                    isTenantCompatibleForSignIn: true,
                    tenantCompatibilityMessage: undefined
                });
                return;
            }

            if (!result.installedTenantName) {
                this.completeTenantCompatibilityCheck(checkToken, {
                    isTenantCompatibilityChecking: false,
                    isTenantCompatibleForSignIn: false,
                    tenantCompatibilityMessage: "Windows Service is installed, but tenant information is missing."
                });
                return;
            }

            const matches = this.areTenantNamesCompatible(tenant, result.installedTenantName);
            this.completeTenantCompatibilityCheck(checkToken, {
                isTenantCompatibilityChecking: false,
                isTenantCompatibleForSignIn: matches,
                tenantCompatibilityMessage: matches
                    ? undefined
                    : `iDART has been configured for tenant '${result.installedTenantName}', signing into '${tenant}' will reconfigure iDART for the new tenant. Unsynced data will be lost`
            });
        } catch {
            this.completeTenantCompatibilityCheck(checkToken, {
                isTenantCompatibilityChecking: false,
                isTenantCompatibleForSignIn: false,
                tenantCompatibilityMessage: "Unable to verify installed Background Service tenant. Sign-in will re-check before continuing."
            });
        }
    }

    private async ensureServiceUninstalledForTenantSwitchAsync(selectedTenant: string): Promise<boolean> {
        try {
            if (!MainCGI.isServiceAvailable()) {
                return true;
            }

            const serviceUpdateApi = window.serviceUpdateApi;
            if (!serviceUpdateApi || !serviceUpdateApi.getInstalledServiceTenantStatus) {
                return true;
            }

            const rawStatus = await serviceUpdateApi.getInstalledServiceTenantStatus();
            const status = this.parseInstalledServiceTenantStatus(rawStatus);

            if (status.error) {
                this.setState({
                    isLoginInProgress: false,
                    loginProgressMessage: undefined,
                    loginErrorMessage: "Unable to verify installed Background Service tenant."
                });
                return false;
            }

            if (status.cleanupPending) {
                if (!serviceUpdateApi.uninstallWindowsServiceForTenantSwitch) {
                    this.setState({
                        isLoginInProgress: false,
                        loginProgressMessage: undefined,
                        loginErrorMessage: "Background Service cleanup is incomplete, but retry is not available."
                    });
                    return false;
                }

                this.setState({ loginProgressMessage: "Retrying Background Service cleanup..." });
                const cleanupResult: any = await serviceUpdateApi.uninstallWindowsServiceForTenantSwitch();
                const cleanupSucceeded = cleanupResult?.success ?? cleanupResult?.Success ?? false;
                if (cleanupSucceeded) {
                    this.setState({
                        isTenantCompatibleForSignIn: true,
                        tenantCompatibilityMessage: undefined
                    });
                    return true;
                }

                const cleanupError = cleanupResult?.error ?? cleanupResult?.Error ?? cleanupResult?.reason ?? cleanupResult?.Reason ?? "Failed to complete Background Service cleanup.";
                this.setState({
                    isLoginInProgress: false,
                    loginProgressMessage: undefined,
                    loginErrorMessage: cleanupError
                });
                return false;
            }

            if (!status.isInstalled) {
                return true;
            }

            if (!status.installedTenantName) {
                this.setState({
                    isLoginInProgress: false,
                    loginProgressMessage: undefined,
                    loginErrorMessage: "Background Service is installed, but tenant details are missing."
                });
                return false;
            }

            if (this.areTenantNamesCompatible(selectedTenant, status.installedTenantName)) {
                return true;
            }

            if (!serviceUpdateApi.uninstallWindowsServiceForTenantSwitch) {
                this.setState({
                    isLoginInProgress: false,
                    loginProgressMessage: undefined,
                    loginErrorMessage: "Tenant changed but Background Service uninstall is not available."
                });
                return false;
            }

            this.setState({ loginProgressMessage: "Switching tenant: uninstalling Background Service..." });
            const uninstallResult: any = await serviceUpdateApi.uninstallWindowsServiceForTenantSwitch();
            const uninstallSucceeded = uninstallResult?.success ?? uninstallResult?.Success ?? false;
            if (uninstallSucceeded) {
                this.setState({
                    isTenantCompatibleForSignIn: true,
                    tenantCompatibilityMessage: undefined
                });
                return true;
            }

            const error = uninstallResult?.error ?? uninstallResult?.Error ?? uninstallResult?.reason ?? uninstallResult?.Reason ?? "Failed to uninstall Background Service for tenant switch.";
            this.setState({
                isLoginInProgress: false,
                loginProgressMessage: undefined,
                loginErrorMessage: error
            });
            return false;
        } catch {
            this.setState({
                isLoginInProgress: false,
                loginProgressMessage: undefined,
                loginErrorMessage: "Failed to uninstall Background Service for tenant switch."
            });
            return false;
        }
    }

    render() {
        const { isAuthenticated, isAuthenticating, isInitialLoad, activeView, } = this.state;
        const myTheme = createTheme({
            palette: {
                primary: {
                    main: "#EC6F41"
                },
                secondary: {
                    main: "#555"
                }
            }
        });

        const styleState = !isAuthenticated && !isAuthenticating && !isInitialLoad && !(["offlineDashboard", "rrc", "spider", "installConfig", "rdcScanning", "wandScanningOffline", "keyConfig"].includes(activeView));

        return <ThemeProvider theme={myTheme}>
            <div 
                className={`${prefixCls}`}
                style={{ borderColor: !styleState ? "#EC6F41" : "" ,
                    borderStyle: !styleState ? "solid" : "" ,
                    borderWidth: !styleState ? "4px 0px 0px" : "",
                    height: !styleState ? "calc(100vh - 4px)" : "100vh",
                    width: "100%",
                    overflowY: "hidden"
                }}
                onPointerDown={ev => {
                    if (ev.pointerType === "mouse") {
                        setTimeout(() => this.setState({ tabletMode: false }), 200);
                        localStorage.setItem("tabletMode", "false");
                    } else if (ev.pointerType === "touch") {
                        setTimeout(() => this.setState({ tabletMode: true }), 200);
                        localStorage.setItem("tabletMode", "true");
                    }
                }}
            >
                {this.main()}
            </div>
        </ThemeProvider>;
    }

    main() {
        const { isAuthenticated, isAuthenticating, isInitialLoad, activeView, loginErrorMessage,
            serverConnectionDetected, syncErrors, manifest, showFirstLoginModal, currentUsername,
            pendingMicrosoftLinking, microsoftLinkingFailed, pendingLinkState } = this.state;

        // Show loading while Microsoft linking is in progress
        if (pendingMicrosoftLinking) {
            const microsoftOAuthApi = (window as any).microsoftOAuthApi;
            return (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh" }}>
                    <CircularProgress />
                    <p style={{ marginTop: 16 }}>Completing account setup...</p>
                    {microsoftOAuthApi && (
                        <Button
                            label="Cancel"
                            onClick={() => microsoftOAuthApi.close()}
                        />
                    )}
                </div>
            );
        }

        // Show error screen if Microsoft linking failed
        if (microsoftLinkingFailed && pendingLinkState) {
            return (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh" }}>
                    <Alert severity="warning" style={{ maxWidth: 500, marginBottom: 16 }}>
                        Your Microsoft account could not be linked to this iDART account. This may be because it is already associated with another account. You can continue using iDART with a local password only.
                    </Alert>
                    {this.state.microsoftLinkingErrorDetail && (
                        <p style={{ fontSize: "0.8em", color: "#666", maxWidth: 500, marginBottom: 16, fontFamily: "monospace" }}>
                            Detail: {this.state.microsoftLinkingErrorDetail}
                        </p>
                    )}
                    <Button
                        label="Continue without Microsoft"
                        onClick={() => this.handleLinkingFallback()}
                    />
                </div>
            );
        }

        // Show first login modal if user needs to complete setup
        if (showFirstLoginModal && currentUsername) {
            return (
                <FirstLoginModal
                    shouldBeShown={true}
                    username={currentUsername}
                    currentEmail={this.state.currentEmail}
                    showMicrosoftLogin={manifest.showMicrosoftLogin || Services.getChannel() !== "production"}
                    onSetupComplete={() => this.onFirstLoginComplete()}
                    onRedirectToMicrosoft={() => this.goToMicrosoftLogin()}
                />
            );
        }

        if (!isAuthenticated && !isAuthenticating && !isInitialLoad && !(["offlineDashboard", "rrc", "spider", "installConfig", "rdcScanning", "wandScanningOffline", "keyConfig"].includes(activeView))) {
            return (
                <>
                    <LoginFormModalPanel
                        shouldBeShown={true}
                        onRequestOfflineView={view => this.setState({ activeView: view })}
                        onRequestOfflineDeviceView={(view, localDbName) => this.onRequestOfflineDeviceView(view, localDbName)}
                        onLoginClick={this.onLoginClick.bind(this)}
                        errorMessage={loginErrorMessage} // TODO: this needed?
                        allowLogin={serverConnectionDetected}
                        onTenantChosen={this.onTenantChosen.bind(this)}
                        isTenantCompatibleForSignIn={this.state.isTenantCompatibleForSignIn}
                        tenantCompatibilityMessage={this.state.tenantCompatibilityMessage}
                        isTenantCompatibilityChecking={this.state.isTenantCompatibilityChecking}
                        isLoginInProgress={this.state.isLoginInProgress}
                        loginProgressMessage={this.state.loginProgressMessage}
                        showWorkOffline={MainCGI.isServiceAvailable()}
                        manifest={manifest}
                        showLegacyCleanupNotice={this.state.showLegacyCleanupNotice}
                        isLegacyCleanupRunning={this.state.isLegacyCleanupRunning}
                        legacyCleanupError={this.state.legacyCleanupError}
                        onRunLegacyCleanup={() => this.runLegacyCleanupAsync()}
                    />
                    {/* Unsynced Data Notification - shows on login page when legacy cleanup is blocked */}
                    <UnsyncedDataNotification
                        isVisible={this.state.showUnsyncedDataNotification}
                        unsyncedReadings={this.state.unsyncedReadings}
                        unsyncedPayloads={this.state.unsyncedPayloads}
                        onDismiss={() => this.setState({ showUnsyncedDataNotification: false })}
                    />
                    {this.idart2DeviceSyncModal()}
                    {this.offlineDeviceSyncCheckErrorModal()}
                </>
            );
        }

        if (!this.state.isAuthenticated && this.state.activeView === "dashboard") return null;
        return (
            <>
                <ModalPanel
                    shouldBeShown={this.state.showingErrors}
                    onClose={() => this.setState({ showingErrors: false })}
                    content={<div style={{ width: 800 }}>{JSON.stringify(this.state.syncErrors, null, 2)}</div>}
                />
                {this.idart2DeviceSyncModal()}
                {this.offlineDeviceSyncCheckErrorModal()}
                {this.onlineDeviceSyncCompatibilityModal()}
                <FlexibleAndFixedPanel
                    topFixedContent={(syncErrors || this.state.showIdart2ServiceWarning) ?
                        <>
                            {syncErrors ?
                                <Alert severity="error">
                                    {syncErrors.some((e: any) => e?.ClassName === "Inductosense.UniversalApp.MainCgi.PermissionDeniedException") ?
                                        "Some data could not be synced because your account does not have permission to make changes. Contact your administrator if you believe this is incorrect." :
                                        "Upload and/or download of data failed"
                                    } &nbsp;
                                    <Link href="#" onClick={(ev: React.MouseEvent) => {
                                        ev.preventDefault();
                                        this.setState({ showingErrors: true })
                                    }}>
                                        Show details
                                    </Link>
                                </Alert> : null}
                            <Idart2ServiceWarningNotification
                                isVisible={this.state.showIdart2ServiceWarning}
                                idart1TenantName={this.state.tenantName}
                                serviceTenantName={this.state.idart2ServiceTenantName}
                                onDismiss={() => this.setState({ showIdart2ServiceWarning: false })}
                            />
                        </> : undefined
                    }
                    flexibleContent={this.activeView()}
                />
                <UiBlockingOverlay uiBlockingState={this.state.uiBlockingState} />

                {/* Service Installation Notification */}
                <ServiceInstallNotification
                    isVisible={this.state.serviceInstallState !== "idle"}
                    installState={this.state.serviceInstallState}
                    errorMessage={this.state.serviceInstallError}
                    installedVersion={this.state.serviceInstalledVersion}
                    onAccept={() => this.retryServiceInstall()}
                    onDismiss={() => this.dismissServiceInstallNotification()}
                />

                {/* V3 Release Notification - shows for users with V3 devices + approved hardware */}
                <V3ReleaseNotification
                    isVisible={this.state.showV3ReleaseNotification && this.state.serviceInstallState === "idle"}
                    onInstall={() => this.handleV3ServiceInstall()}
                />

                {/* Unapproved Hardware Notification - shows for users with V3 devices but unapproved hardware */}
                <UnapprovedHardwareNotification
                    isVisible={this.state.showUnapprovedHardwareNotification}
                    deviceSerialNumber={this.state.unapprovedHardwareDeviceSerialNumber}
                    authToken={this.state.unapprovedHardwareAuthToken}
                    apiUrl={this.state.unapprovedHardwareApiUrl}
                    onDismiss={() => {
                        this.setState({
                            showUnapprovedHardwareNotification: false,
                            unapprovedHardwareDeviceSerialNumber: undefined,
                            unapprovedHardwareAuthToken: undefined,
                            unapprovedHardwareApiUrl: undefined
                        });
                    }}
                />

                {/* Unsynced Data Notification - shows when legacy cleanup is blocked by unsynced data */}
                <UnsyncedDataNotification
                    isVisible={this.state.showUnsyncedDataNotification}
                    unsyncedReadings={this.state.unsyncedReadings}
                    unsyncedPayloads={this.state.unsyncedPayloads}
                    onDismiss={() => this.setState({ showUnsyncedDataNotification: false })}
                />
            </>
        );
    }

    async dbUpdate(ForceReUpload = false) {

        if (MainCGI.isServiceAvailable()) {

            this.setState({ isSyncingData: true });

            // Workaround until we fix the token issued immediatley being invalid
            // The .NET code can't refresh the token in response to a 401
            // So we send an alive message to ensure authentication headers for DB update
            await Services.sendAliveMessage();

            const syncResult = await MainCGI.uploadDbAuto(ForceReUpload);
            this.setState({ isSyncingData: false, isShowingDbUpdateModal: false });

            // TODO: Unsubscribe status updates...
            console.log("syncResult", syncResult);

            if (syncResult && syncResult.syncErrors.length > 0) {
                this.setState({ syncErrors: syncResult.syncErrors });
            }
        }

        this.setState({ isSyncingData: false, isShowingDbUpdateModal: false }); // TODO: Should have this??
    }

    async onLoginClick(refreshToken: string, tenantName: string, availableTenants: string[], rememberMe: boolean) {
        if (this.loginInProgressLock || this.state.isLoginInProgress) {
            return;
        }
        this.loginInProgressLock = true;

        try {
            this.setState({
                loginErrorMessage: undefined,
                isLoginInProgress: true,
                loginProgressMessage: "Signing in..."
            });

            const canProceed = await this.ensureServiceUninstalledForTenantSwitchAsync(tenantName);
            if (!canProceed) {
                return;
            }

            this.setState({ loginProgressMessage: "Signing in..." });
            await Services.completeAuthentication(refreshToken, tenantName, rememberMe);

            // Check if user needs first-login setup (from JWT claim)
            if (Services.needsFirstLoginSetup()) {
                const username = Services.getUsername();

                // Try to get user's current email (may fail if no permission)
                let currentEmail: string | undefined;
                try {
                    const user: User = await Services.UsersService.getUser(username || "");
                    currentEmail = user.email;
                } catch {
                    // User doesn't have permission to view their profile - they'll need to provide email
                    currentEmail = undefined;
                }

                this.setState({
                    showFirstLoginModal: true,
                    currentUsername: username || "",
                    currentEmail,
                    uiSettings: Services.getUiSettings(),
                    isLoginInProgress: false,
                    loginProgressMessage: undefined,
                    isAuthenticating: false,
                    isInitialLoad: false,
                    availableTenants,
                    tenantName
                });
                return; // Don't proceed until first-login setup is complete
            }

            this.setState({
                uiSettings: Services.getUiSettings(),
                isAuthenticated: true,
                deviceSyncCompatibilityStatus: "checking",
                isLoginInProgress: false,
                loginProgressMessage: undefined,
                isAuthenticating: false,
                isInitialLoad: false,
                availableTenants,
                tenantName
            });

            await this.dbUpdate();

            // Check if Windows Service is required (V3 devices + approved hardware)
            await this.checkWindowsServiceRequiredAsync(tenantName);

        } catch (exception) {
            // A typeerror is thrown when the server couldn't be reached at all
            if (exception instanceof Response && exception.status === 403) {
                this.setState({
                    isLoginInProgress: false,
                    loginProgressMessage: undefined,
                    isAuthenticating: false,
                    loginErrorMessage: "Incorrect username/password"
                })
            } else if (exception instanceof Response && exception.status === 404) {
                this.setState({
                    isLoginInProgress: false,
                    loginProgressMessage: undefined,
                    isAuthenticating: false,
                    loginErrorMessage: "Incorrect server URL"
                })
            } else if (exception instanceof Response && exception.status === 400) { // TODO: Check exact error
                this.setState({
                    isLoginInProgress: false,
                    loginProgressMessage: undefined,
                    isAuthenticating: false,
                    loginErrorMessage: "Please enter a tenant in Advanced Settings"
                })
            } else {
                this.setState({
                    isLoginInProgress: false,
                    loginProgressMessage: undefined
                });
                throw exception;
            }
        } finally {
            this.loginInProgressLock = false;
        }
        //this.props.onReadyToRender();
    }

    /**
     * Checks for unsynced data in the legacy LocalIdart2Db database.
     * Shows a warning notification if data needs to sync before the update can complete.
     */
    async checkUnsyncedDataAsync() {
        // Only run in desktop app
        if (!MainCGI.isServiceAvailable()) {
            return;
        }

        // Check if serviceUpdateApi is available
        const serviceUpdateApi = (window as any).serviceUpdateApi;
        if (!serviceUpdateApi || !serviceUpdateApi.checkUnsyncedData) {
            console.log("Service update API checkUnsyncedData not available");
            return;
        }

        try {
            const result: UnsyncedDataCheckResult = await serviceUpdateApi.checkUnsyncedData();
            console.log("Unsynced data check result:", result);

            if (result.error) {
                console.error("Unsynced data check failed:", result.error);
                return;
            }

            if (result.hasUnsyncedData && result.needsLegacyCleanup) {
                // User has unsynced data that must sync before cleanup can proceed
                console.log(`Unsynced data blocking cleanup: ${result.unsyncedReadings} readings, ${result.unsyncedPayloads} payloads`);
                this.setState({
                    showUnsyncedDataNotification: true,
                    unsyncedReadings: result.unsyncedReadings,
                    unsyncedPayloads: result.unsyncedPayloads,
                    showLegacyCleanupNotice: false,
                    legacyCleanupError: undefined
                });
            } else if (result.needsLegacyCleanup) {
                this.setState({
                    showUnsyncedDataNotification: false,
                    unsyncedReadings: 0,
                    unsyncedPayloads: 0,
                    showLegacyCleanupNotice: true,
                    legacyCleanupError: undefined
                });
            } else {
                // No unsynced data or no cleanup needed
                this.setState({
                    showUnsyncedDataNotification: false,
                    unsyncedReadings: 0,
                    unsyncedPayloads: 0,
                    showLegacyCleanupNotice: false,
                    legacyCleanupError: undefined
                });
            }
        } catch (error) {
            console.error("Unsynced data check failed:", error);
        }
    }

    async runLegacyCleanupAsync() {
        if (this.state.isLegacyCleanupRunning || !MainCGI.isServiceAvailable()) {
            return;
        }

        this.setState({
            isLegacyCleanupRunning: true,
            legacyCleanupError: undefined
        });

        try {
            const result = await MainCGI.send<LegacyCleanupResult>("runPostUpdateCleanup", {});
            const success = result.success ?? false;
            const skipped = result.skipped ?? false;
            const reason = result.reason ?? "";
            const error = result.error;

            if (success && !skipped) {
                this.setState({
                    showLegacyCleanupNotice: false,
                    isLegacyCleanupRunning: false,
                    legacyCleanupError: undefined
                });
                return;
            }

            this.setState({
                isLegacyCleanupRunning: false,
                showLegacyCleanupNotice: true,
                legacyCleanupError: error || reason || "Legacy cleanup did not complete. Please try again."
            });
        } catch (error) {
            this.setState({
                isLegacyCleanupRunning: false,
                showLegacyCleanupNotice: true,
                legacyCleanupError: String(error)
            });
        }
    }

    /**
     * Checks if the tenant has V3 devices and approved hardware.
     * Does NOT trigger installation - just shows notification if required.
     */
    async checkWindowsServiceRequiredAsync(tenantName: string) {
        if (!Services.userCanEdit()) {
            this.setState({ deviceSyncCompatibilityStatus: "allowed" }, () => this.resumePendingOnlineDeviceScan());
            return;
        }

        // Only run in desktop app
        if (!MainCGI.isServiceAvailable()) {
            this.setState({ deviceSyncCompatibilityStatus: "allowed" }, () => this.resumePendingOnlineDeviceScan());
            return;
        }

        // Check if serviceUpdateApi is available
        const serviceUpdateApi = (window as any).serviceUpdateApi;
        if (!serviceUpdateApi || !serviceUpdateApi.checkWindowsServiceRequired) {
            console.log("Service update API not available");
            this.setState({ deviceSyncCompatibilityStatus: "unavailable" });
            return;
        }

        let checkingTimerId: number | null = null;
        const clearCheckingTimer = () => {
            if (checkingTimerId !== null) {
                window.clearTimeout(checkingTimerId);
                checkingTimerId = null;
            }
        };

        try {
            // Delay showing "checking" to avoid brief flicker on fast checks.
            checkingTimerId = window.setTimeout(() => {
                this.setState({ serviceInstallState: "checking" });
            }, this.SERVICE_CHECKING_SHOW_DELAY_MS);

            // Get auth info
            const authToken = Services.getLatestToken();
            const urlPrefix = await Services.getUrlPrefix();

            if (!authToken) {
                console.log("No auth token available for service check");
                clearCheckingTimer();
                this.setState({ serviceInstallState: "idle", deviceSyncCompatibilityStatus: "unavailable" });
                return;
            }

            // Check if service is required (V3 devices + approved hardware + not installed)
            const result: WindowsServiceRequiredResult = await serviceUpdateApi.checkWindowsServiceRequired({
                authToken,
                urlPrefix,
                tenant: tenantName
            });

            console.log("Windows Service required check result:", result);

            if (result.error) {
                console.error("Windows Service required check failed:", result.error);
                clearCheckingTimer();
                this.setState({ serviceInstallState: "idle", deviceSyncCompatibilityStatus: "unavailable" });
                return;
            }

            if (result.isIdart2ServiceInstalled) {
                console.log("iDART2-mode Windows Service detected - showing warning");
                clearCheckingTimer();
                const hasPendingOnlineDeviceScan = this.state.pendingOnlineDeviceView !== undefined;
                this.setState({
                    serviceInstallState: "idle",
                    deviceSyncCompatibilityStatus: "idart2",
                    showOnlineDeviceSyncCompatibilityModal: false,
                    pendingOnlineDeviceView: undefined,
                    showIdart2DeviceSyncModal: hasPendingOnlineDeviceScan || this.state.showIdart2DeviceSyncModal,
                    showIdart2ServiceWarning: true,
                    isIdart2ServiceInstalled: true,
                    idart2ServiceTenantName: result.idart2ServiceTenantName,
                    showV3ReleaseNotification: false,
                    showUnapprovedHardwareNotification: false,
                    unapprovedHardwareDeviceSerialNumber: undefined
                }, () => this.resumePendingOnlineDeviceScan());
            } else if (result.serviceRequired) {
                // User has V3 devices + approved hardware + service not installed
                // Show the V3 notification prompting them to install
                console.log("Windows Service installation required - showing notification");
                clearCheckingTimer();
                this.setState({
                    serviceInstallState: "idle",
                    deviceSyncCompatibilityStatus: "allowed",
                    showIdart2ServiceWarning: false,
                    isIdart2ServiceInstalled: false,
                    idart2ServiceTenantName: undefined,
                    showV3ReleaseNotification: true,
                    showUnapprovedHardwareNotification: false,
                    unapprovedHardwareDeviceSerialNumber: undefined
                }, () => this.resumePendingOnlineDeviceScan());
            } else if (result.hasV3Devices && !result.isApproved && !result.isInstalled) {
                // User has V3 devices but hardware is not approved
                // Show notification to contact Inductosense (only if not previously dismissed)
                const dismissed = localStorage.getItem("unapprovedHardwareNotificationDismissed") === "true";
                console.log("V3 devices detected but hardware not approved, dismissed:", dismissed);
                clearCheckingTimer();
                this.setState({
                    serviceInstallState: "idle",
                    deviceSyncCompatibilityStatus: "allowed",
                    showIdart2ServiceWarning: false,
                    isIdart2ServiceInstalled: false,
                    idart2ServiceTenantName: undefined,
                    showV3ReleaseNotification: false,
                    showUnapprovedHardwareNotification: true,
                    unapprovedHardwareDeviceSerialNumber: result.deviceSerialNumber,
                    unapprovedHardwareAuthToken: authToken,
                    unapprovedHardwareApiUrl: urlPrefix
                }, () => this.resumePendingOnlineDeviceScan());
            } else {
                // Windows Service not required or already installed
                console.log("Windows Service not required:", {
                    hasV3Devices: result.hasV3Devices,
                    isApproved: result.isApproved,
                    isInstalled: result.isInstalled
                });
                clearCheckingTimer();
                this.setState({
                    serviceInstallState: "idle",
                    deviceSyncCompatibilityStatus: "allowed",
                    showIdart2ServiceWarning: false,
                    isIdart2ServiceInstalled: false,
                    idart2ServiceTenantName: undefined,
                    showV3ReleaseNotification: false,
                    showUnapprovedHardwareNotification: false,
                    unapprovedHardwareDeviceSerialNumber: undefined
                }, () => this.resumePendingOnlineDeviceScan());
            }
        } catch (error) {
            console.error("Windows Service required check failed:", error);
            clearCheckingTimer();
            this.setState({ serviceInstallState: "idle", deviceSyncCompatibilityStatus: "unavailable" });
        }
    }

    /**
     * Triggers the actual service installation with UAC prompt.
     * Called when user clicks "Yes, Install" on the V3 notification.
     */
    async installServiceAsync() {
        if (!Services.userCanEdit()) {
            return;
        }

        const tenantName = this.state.tenantName;
        if (!tenantName) {
            console.error("No tenant name available for service installation");
            return;
        }

        const serviceUpdateApi = (window as any).serviceUpdateApi;
        if (!serviceUpdateApi || !serviceUpdateApi.checkAndInstall) {
            console.log("Service update API not available");
            return;
        }

        try {
            // Show installing notification
            this.setState({
                showV3ReleaseNotification: false,
                serviceInstallState: "installing"
            });

            // Get auth info
            const authToken = Services.getLatestToken();
            const urlPrefix = await Services.getUrlPrefix();

            console.log("Service install token (last 10 chars):", authToken?.slice(-10));
            console.log("Token length:", authToken?.length);

            if (!authToken) {
                console.log("No auth token available for service installation");
                this.setState({ serviceInstallState: "idle" });
                return;
            }

            // Call the service installation (triggers UAC prompt)
            const result: ServiceInstallResult = await serviceUpdateApi.checkAndInstall({
                authToken,
                urlPrefix,
                tenant: tenantName
            });

            // Handle result
            if (result.success) {
                if (result.skipped) {
                    // Service not needed or already up to date
                    console.log("Service installation skipped:", result.reason);
                    this.setState({ serviceInstallState: "idle" });
                } else {
                    // Successfully installed
                    this.setState({
                        serviceInstallState: "success",
                        serviceInstalledVersion: result.installedVersion
                    });
                }
            } else {
                if (result.reason === "UAC cancelled by user") {
                    // User cancelled UAC - show deferred notification, don't re-show blue banner until next login
                    this.setState({
                        serviceInstallState: "deferred"
                    });
                } else {
                    this.setState({
                        serviceInstallState: "error",
                        serviceInstallError: result.error || result.reason
                    });
                }
            }
        } catch (error) {
            console.error("Service installation failed:", error);
            this.setState({
                serviceInstallState: "error",
                serviceInstallError: String(error)
            });
        }
    }

    /**
     * Retry service installation after deferral or error.
     */
    async retryServiceInstall() {
        await this.installServiceAsync();
    }

    /**
     * Dismiss service install notification.
     */
    dismissServiceInstallNotification() {
        this.setState({ serviceInstallState: "idle" });
    }

    /**
     * Handle user clicking "Yes, Install" on V3 notification.
     * This triggers the UAC prompt for service installation.
     */
    handleV3ServiceInstall() {
        this.installServiceAsync();
    }

    async onFirstLoginComplete() {
        this.setState({
            showFirstLoginModal: false,
            isAuthenticated: true,
            deviceSyncCompatibilityStatus: "checking",
            currentUsername: undefined,
            pendingMicrosoftLinking: false
        });

        await this.dbUpdate();
        await this.checkWindowsServiceRequiredAsync(this.state.tenantName!);
    }

    private getRedirectUri() {
        return location.origin === "file://" ? "http://localhost:5000/" : location.origin;
    }

    private goToMicrosoftLogin() {
        const queryString = new URLSearchParams({
            login_hint: "",
            response_type: "code",
            client_id: "b8f5a2b8-79d6-4d05-a766-3cd6141cfbe3",
            redirect_uri: this.getRedirectUri(),
            nonce: "",
            scope: "profile"
        }).toString();

        const oauthUrl = "https://login.microsoftonline.com/organizations/oauth2/authorize?" + queryString;
        const microsoftOAuthApi = (window as any).microsoftOAuthApi;

        if (microsoftOAuthApi) {
            // Desktop app: open Microsoft login in a popup window so the main window stays usable
            this.setState({ showFirstLoginModal: false, pendingMicrosoftLinking: true });

            microsoftOAuthApi.onCode((result: { code: string | null, cancelled: boolean }) => {
                microsoftOAuthApi.removeCodeListener();
                if (result.cancelled || !result.code) {
                    this.handleOAuthCancelled();
                } else {
                    this.handleOAuthCode(result.code);
                }
            });

            microsoftOAuthApi.open(oauthUrl);
        } else {
            // Web app: navigate the whole page (browser back button is available)
            location.href = oauthUrl;
        }
    }

    private async checkMicrosoftLinkingReturn() {
        // Only used by the web flow where Microsoft redirects back to the app URL with ?code=
        const linkStateJson = localStorage.getItem(LINK_MICROSOFT_KEY);
        if (!linkStateJson) return;

        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");

        if (!code) return;

        let linkState: LinkMicrosoftState;
        try {
            linkState = JSON.parse(linkStateJson);
        } catch {
            localStorage.removeItem(LINK_MICROSOFT_KEY);
            return;
        }

        localStorage.removeItem(LINK_MICROSOFT_KEY);

        if (location.protocol !== "file:") {
            history.replaceState(null, "", "/");
        }

        this.setState({ pendingMicrosoftLinking: true });
        await this.performMicrosoftLinking(code, linkState);
    }

    // Called by the desktop popup flow when the OAuth code arrives via IPC
    private async handleOAuthCode(code: string) {
        const linkStateJson = localStorage.getItem(LINK_MICROSOFT_KEY);
        localStorage.removeItem(LINK_MICROSOFT_KEY);
        if (!linkStateJson) return;

        let linkState: LinkMicrosoftState;
        try {
            linkState = JSON.parse(linkStateJson);
        } catch {
            this.setState({ pendingMicrosoftLinking: false });
            return;
        }

        await this.performMicrosoftLinking(code, linkState);
    }

    // Called when the user cancels the Microsoft OAuth popup
    private handleOAuthCancelled() {
        const linkStateJson = localStorage.getItem(LINK_MICROSOFT_KEY);
        localStorage.removeItem(LINK_MICROSOFT_KEY);

        if (!linkStateJson) {
            this.setState({ pendingMicrosoftLinking: false });
            return;
        }

        let linkState: LinkMicrosoftState;
        try {
            linkState = JSON.parse(linkStateJson);
        } catch {
            this.setState({ pendingMicrosoftLinking: false });
            return;
        }

        this.setState({
            pendingMicrosoftLinking: false,
            microsoftLinkingFailed: true,
            microsoftLinkingErrorDetail: "Microsoft sign-in was cancelled",
            pendingLinkState: linkState
        });
    }

    private async performMicrosoftLinking(code: string, linkState: LinkMicrosoftState) {
        try {
            if (!linkState.refreshToken || !linkState.tenant) {
                throw new Error("Missing refresh token or tenant in link state");
            }

            await Services.initialiseAuthentication();
            await Services.completeAuthentication(linkState.refreshToken, linkState.tenant, true);

            const linkResult = await Services.linkExternalAccount(code, this.getRedirectUri());

            if (linkResult.success) {
                console.log("Microsoft account linked successfully, OID:", linkResult.externalLoginId);
                this.setState({
                    pendingMicrosoftLinking: false,
                    isAuthenticated: true,
                    deviceSyncCompatibilityStatus: "checking",
                    isAuthenticating: false,
                    isInitialLoad: false,
                    uiSettings: Services.getUiSettings(),
                    tenantName: linkState.tenant
                });
                await this.dbUpdate();
                await this.checkWindowsServiceRequiredAsync(linkState.tenant);
            } else {
                console.warn("Microsoft linking returned success: false — showing error to user");
                this.setState({ pendingMicrosoftLinking: false, microsoftLinkingFailed: true, microsoftLinkingErrorDetail: "Server returned success: false", pendingLinkState: linkState });
            }
        } catch (ex) {
            console.error("Error during Microsoft linking:", ex);
            let detail: string;
            if (ex instanceof Response) {
                detail = `HTTP ${ex.status} ${ex.statusText} from ${ex.url}`;
            } else if (ex instanceof Error) {
                detail = ex.message;
            } else {
                detail = String(ex);
            }
            this.setState({ pendingMicrosoftLinking: false, microsoftLinkingFailed: true, microsoftLinkingErrorDetail: detail, pendingLinkState: linkState });
        }
    }

    private async handleLinkingFallback() {
        const { pendingLinkState } = this.state;
        if (!pendingLinkState) return;
        this.setState({ microsoftLinkingFailed: false, microsoftLinkingErrorDetail: undefined, pendingLinkState: undefined, pendingMicrosoftLinking: true });
        try {
            await this.setUserAsLocalAndComplete(pendingLinkState);
        } catch (error) {
            console.error("Failed to set user as LOCAL:", error);
            this.setState({ pendingMicrosoftLinking: false });
        }
    }

    private async setUserAsLocalAndComplete(linkState: LinkMicrosoftState) {
        try {
            // Ensure we're authenticated
            if (!Services.isSessionActive()) {
                await Services.initialiseAuthentication();
                await Services.completeAuthentication(linkState.refreshToken, linkState.tenant, true);
            }

            // Update user to LOCAL
            const uiSettings = Services.getUiSettings();
            const userUpdate: Userupdate = {
                externalLoginId: "LOCAL",
                unitsMode: uiSettings.unitsMode === "metric" ? Units.Metric : Units.Imperial,
                temperatureUnits: uiSettings.temperatureUnits === "celsius" ? Temperatureunits.Celsius : Temperatureunits.Fahrenheit
            };
            await Services.UsersService.updateUser(linkState.username, userUpdate);

            // Complete login
            this.setState({
                pendingMicrosoftLinking: false,
                isAuthenticated: true,
                deviceSyncCompatibilityStatus: "checking",
                isAuthenticating: false,
                isInitialLoad: false,
                uiSettings: Services.getUiSettings(),
                tenantName: linkState.tenant
            });
            await this.dbUpdate();
            await this.checkWindowsServiceRequiredAsync(linkState.tenant);
        } catch (error) {
            console.error("Failed to set user as LOCAL:", error);
            throw error;
        }
    }

    activeView() {
        // TODO: Store UI settings offline
        const uiSettings: UiSettings = this.state.uiSettings !== undefined ?
            this.state.uiSettings :
            { unitsMode: "metric", temperatureUnits: "celsius" };

        const onClose = () => this.changeActiveView("dashboard");

        const showLines = this.state.progressTextLines.slice(-5);

        const dbSyncModal = <ModalPanel
            showBranding
            shouldBeShown={true}
            content={
                <>
                    <div style={{ textAlign: "center" }}><CircularProgress /></div>
                    <ul>{showLines.map((p, i) => {
                            return <li style={{ listStyleType: "square", opacity: 1 - (showLines.length - (i + 1)) / 5 }} key={i}>{p}</li>
                        })}
                    </ul>
                </>}
        />;

        if (this.state.isShowingDbUpdateModal) {
            return dbSyncModal;
        }

        switch (this.state.activeView) {
            case "dashboard": return <MainDashboard
                key={this.state.tenantName}
                isSyncingData={this.state.isSyncingData}
                tabletMode={this.state.tabletMode}
                changeActiveView={newView => this.changeActiveView(newView)}
                uiSettings={uiSettings}
                onSignOut={async () => {
                    // TODO: Check finished sync

                    Services.clearTokens();
                    Services.setSessionActive(false);
                    Services.ClearAuthenticationTokens();
                    this.setState({
                        isAuthenticating: false,
                        isAuthenticated: false,
                        isInitialLoad: false,
                        showV3ReleaseNotification: false,
                        showUnapprovedHardwareNotification: false,
                        unapprovedHardwareDeviceSerialNumber: undefined,
                        serviceInstallState: "idle"
                    });
                    await this.checkServerAccessible();
                }}
                onAdvancedConfig={() => this.setState({ activeView: "installConfig" })}
                onRequestFullSync={() => this.dbUpdate(true)}
                availableTenants={this.state.availableTenants}
                onTenantChange={newTenant => this.setState({ tenantName: newTenant })}
                tenantName={this.state.tenantName}
                isIdart2ServiceInstalled={this.state.isIdart2ServiceInstalled}
                onIdart2DeviceSyncRequested={() => this.setState({ showIdart2DeviceSyncModal: true })}

            />;
            case "rrc": return <RRCPanel />;
            case "materials": return <MaterialsView onClose={onClose} uiSettings={uiSettings} />;
            case "deployments": return <DeploymentsDashboard  areGatewaysEnabled={this.state.areGatewaysEnabled} onDbUploadRequired={async () => {
                await this.dbUpdate()}} onGatewayClick={()=>{this.changeActiveView("gateways")}} onClose={onClose} manifest={this.state.manifest} uiSettings={uiSettings} />;
            case "installConfig":
                if (this.state.isSyncingData) {
                    console.log("showing sync...");
                    return dbSyncModal;
                }
                return <InstallConfigScreen onClose={() =>
                    this.setState({
                        activeView: "dashboard",
                        isAuthenticated: false,
                        isAuthenticating: false,
                        isInitialLoad: false
                    })}
                    manifest={this.state.manifest}
                />;
            case "rdcTest":
                return <RDCTestPanel onClose={onClose} />;
            case "rdcScanning":
                return <><RDCScanningOverviewForm uiSettings={uiSettings} onClose={onClose} onDbUploadRequired={async () => {
                    await this.dbUpdate();
                }} />{this.state.isSyncingData ? dbSyncModal : null}</>;
            case "wandScanning":
                return <DcScanningOverviewForm uiSettings={uiSettings} onClose={onClose} onDbUploadRequired={async () => {
                    console.log("updating data...");
                    await this.dbUpdate();
                    console.log("updated data");
                }} />;
            case "wandScanningOffline":
                return <DcScanningOverviewForm uiSettings={uiSettings} onClose={onClose} onDbUploadRequired={() => Promise.resolve()} />;
            case "users": return <UserListView manifest={this.state.manifest} onClose={onClose} />;
            case "spider": return <SpiderPanel />;
            case "import": return <ImportDashboard onClose={onClose} />;
            case "export": return <ExportDashboard onClose={onClose} />;
            case "recalculation": return <RecalculationDashboard onClose={onClose} />;
            case "gateways": return <GatewaysView onDbUploadRequired={async () => {await this.dbUpdate()}} onClose={onClose} uiSettings={uiSettings} />;

            default: throw new Error("Unhandled active view encountered.");
        }
    }

    private async changeActiveView(newView: View) {
        if (newView === "recalculation" && !Services.userCanEdit()) {
            return;
        }

        if (["rdcScanning", "wandScanning"].includes(newView) && this.state.deviceSyncCompatibilityStatus !== "allowed") {
            this.setState({
                showOnlineDeviceSyncCompatibilityModal: true,
                pendingOnlineDeviceView: newView as "rdcScanning" | "wandScanning"
            });
            return;
        }

        if (["rdcScanning", "wandScanning"].includes(newView) && this.state.isSyncingData) {
            this.setState({ activeView: newView, isShowingDbUpdateModal: true });
        } else {
            if(newView === "deployments"){
                const gatewayStatus = await Services.Gateways.getGatewayEnabled();
                this.setState({
                    areGatewaysEnabled: gatewayStatus.gatewayEnabled
                });
            }
            this.setState({ activeView: newView });
        }
    }
}

export default TopLevelView;
